import React, { useState, useMemo, useEffect } from "react";
import moment from "moment";
import axios from 'axios';
import { Space, Tooltip, Button, Modal, Form, Input, message } from 'antd';
import { DownloadOutlined, MailOutlined, EyeOutlined } from '@ant-design/icons';
import { useUser } from '../../utils/userContext';

function formatCurrency(amount, currency, excludeSymbol = false) {
    const currencySymbols = {
        USD: '$',
        GBP: '£',
        INR: '₹',
    };
    const symbol = currencySymbols[currency] || '£'; // Default to £ if currency is unknown
    return excludeSymbol
        ? amount.toLocaleString("en-GB", { minimumFractionDigits: 2 })
        : `${symbol}${amount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;
}

function PartnersDashboardPage() {
    const [invoices, setInvoices] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [loading, setLoading] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [generatingInvoice, setGeneratingInvoice] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [emailForm] = Form.useForm();
    const token = localStorage.getItem("token");
    const { user } = useUser();

    useEffect(() => {
        const fetchInvoices = async () => {
            setLoading(true);
            try {
                if (!user || !user.OfficialEmail) {
                    message.error("User not logged in or partner not found. Please log in again.");
                    setLoading(false);
                    return;
                }
                const URL = process.env.REACT_APP_BE_URL + '/getPartnerInvoices';
                const response = await axios.get(URL, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (response.status === 200 && response.data.invoiceDetails) {
                    console.log("API Response:", response.data.invoiceDetails);
                    const mappedInvoices = response.data.invoiceDetails
                        .filter(inv => inv.partner === user.FirstName) // Filter by logged-in user's partner
                        .map(inv => ({
                            invoiceId: inv.invoiceNo,
                            partner: inv.partner,
                            partnerName: inv.partnerName,
                            month: moment(inv.invoiceDate).format("MMMM"),
                            year: moment(inv.invoiceDate).year(),
                            amount: inv.total || 0,
                            paid: inv.status.toLowerCase() === "paid" ? inv.total : 0,
                            status: inv.status.toLowerCase() === "reject" ? "Rejected" : inv.status || "Pending",
                            generatedDate: inv.invoiceDate,
                            invoiceFile: inv.invoicefile,
                            currency: inv.currency,
                        }));
                    setInvoices(mappedInvoices);
                    if (mappedInvoices.length === 0) {
                        message.warning("No invoices found for this partner.");
                    }
                } else {
                    message.error("Failed to fetch invoices: Invalid response status.");
                }
            } catch (error) {
                console.error("Failed to fetch invoices:", error);
                message.error(`Failed to fetch invoices: ${error.message || "Unknown error"}`);
            } finally {
                setLoading(false);
            }
        };
        fetchInvoices();
    }, [user, token]);

    // Summary calculations
    const totalInvoices = invoices.length;
    const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0);
    const paidAmount = invoices.reduce((sum, inv) => sum + (inv.paid || 0), 0);
    const pendingAmount = totalAmount - paidAmount;

    // Filter invoices based on search query
    const filteredInvoices = useMemo(() => {
        return invoices.filter(inv =>
            inv.invoiceId.toLowerCase().includes(searchQuery.toLowerCase()) ||
            inv.partner.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (inv.partnerName && inv.partnerName.toLowerCase().includes(searchQuery.toLowerCase())) ||
            inv.month.toLowerCase().includes(searchQuery.toLowerCase()) ||
            String(inv.year).includes(searchQuery) ||
            String(inv.amount).includes(searchQuery) ||
            inv.status.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery, invoices]);

    // Pagination logic
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredInvoices.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredInvoices.length / recordsPerPage);

    const paginate = pageNumber => setCurrentPage(pageNumber);

    const handle401Error = (error) => {
        if (error?.response?.status === 401 || error.message.includes('401')) {
            message.error("Session expired. Please login again.", {
                position: "topCenter",
            });
            localStorage.removeItem("userInfo");
            window.location.href = "/auth/login";
            return true;
        }
        return false;
    };

    const handleGenerateInvoice = async (invoice) => {
        if (!invoice || !invoice.invoiceFile) {
            message.error(`No invoice file available for ${invoice?.invoiceId || "this invoice"}`);
            return;
        }

        setGeneratingInvoice(true);
        try {
            // Create a new window/tab and load the PDF
            const newWindow = window.open();
            if (newWindow) {
                newWindow.document.write(`
                    <!DOCTYPE html>
                    <html>
                        <head>
                            <title>Invoice ${invoice.invoiceId}</title>
                            <style>
                                body, html {
                                    margin: 0;
                                    padding: 0;
                                    height: 100%;
                                    overflow: hidden;
                                }
                                iframe {
                                    border: none;
                                    width: 100%;
                                    height: 100%;
                                    position: fixed;
                                    top: 0;
                                    left: 0;
                                }
                            </style>
                        </head>
                        <body>
                            <iframe src="${invoice.invoiceFile}"></iframe>
                        </body>
                    </html>
                `);
                message.success(`Invoice ${invoice.invoiceId} opened in new tab`);
            } else {
                message.error("Pop-up blocked. Please allow pop-ups for this site.");
            }
        } catch (error) {
            console.error("Invoice view error:", error);
            if (!handle401Error(error)) {
                message.error(`Failed to open invoice ${invoice.invoiceId}: ${error.message || "Unknown error"}`);
            }
        } finally {
            setGeneratingInvoice(false);
        }
    };

    const handleEmailClick = (invoice) => {
        setSelectedInvoice(invoice);
        setIsEmailModalVisible(true);
        emailForm.resetFields();
    };

    const handleEmailModalCancel = () => {
        setIsEmailModalVisible(false);
        emailForm.resetFields();
    };

    const handleEmailSend = async (values) => {
        setSendingEmail(true);
        try {
            console.log(`Preparing email for invoice: ${selectedInvoice?.invoiceId}`); // Debugging log
            const subject = `Invoice ${selectedInvoice.invoiceId} from BYT`;
            const body = `Please find the details for Invoice No: ${selectedInvoice.invoiceId}\nPartner: ${selectedInvoice.partner}\nPartner Name: ${selectedInvoice.partnerName}\nMonth: ${selectedInvoice.month}\nYear: ${selectedInvoice.year}\nAmount: ${formatCurrency(selectedInvoice.amount, selectedInvoice.currency)}\nStatus: ${selectedInvoice.status}\n\nPlease attach the downloaded invoice file to this email.`;

            // Attempt to download the invoice file if available
            if (selectedInvoice && selectedInvoice.invoiceFile) {
                try {
                    console.log(`Fetching invoice file for email: ${selectedInvoice.invoiceFile}`); // Debugging log
                    const response = await fetch(selectedInvoice.invoiceFile, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
                    if (response.ok) {
                        const blob = await response.blob();
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = `invoice_${selectedInvoice.invoiceId}.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                    } else {
                        const errorText = await response.text();
                        console.warn(`Failed to fetch invoice file: ${response.status} ${response.statusText} - ${errorText}`);
                    }
                } catch (fetchError) {
                    console.error("Failed to fetch invoice file:", fetchError, {
                        invoiceId: selectedInvoice?.invoiceId,
                        invoiceFile: selectedInvoice?.invoiceFile
                    });
                    // Continue to open email client even if fetch fails
                }
            } else {
                console.warn(`No invoice file available for ${selectedInvoice?.invoiceId || 'this invoice'}`);
            }

            // Open email client with pre-filled details
            window.location.href = `mailto:${values.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
            message.success(`Email client opened for ${values.email}. Please attach the downloaded invoice file manually.`);
            setIsEmailModalVisible(false);
            emailForm.resetFields();
        } catch (error) {
            console.error("Email preparation error:", error, {
                invoiceId: selectedInvoice?.invoiceId,
                invoiceFile: selectedInvoice?.invoiceFile
            });
            if (!handle401Error(error)) {
                message.error(`Failed to prepare email for invoice ${selectedInvoice?.invoiceId || 'unknown'}: ${error.message || "Unknown error"}`);
            }
        } finally {
            setSendingEmail(false);
        }
    };

    const handleViewClick = (invoice) => {
        setSelectedInvoice(invoice);
        setIsViewModalVisible(true);
    };

    const handleViewModalCancel = () => {
        setIsViewModalVisible(false);
        setSelectedInvoice(null);
    };

    const renderActions = (invoice) => (
        <Space size={6}>
            <Tooltip title="View Invoice">
                <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    size="small"
                    loading={generatingInvoice && selectedInvoice?.invoiceId === invoice.invoiceId}
                    style={{
                        backgroundColor: "#004f7c",
                        borderColor: "#004f7c",
                        fontSize: "12px",
                        padding: "4px 8px",
                        height: "28px",
                        minWidth: "28px"
                    }}
                    onClick={() => handleGenerateInvoice(invoice)}
                />
            </Tooltip>
            <Tooltip title="Send Email">
                <Button
                    type="default"
                    icon={<MailOutlined />}
                    size="small"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleEmailClick(invoice);
                        return false;
                    }}
                    style={{
                        borderColor: "#004f7c",
                        color: "#004f7c",
                        fontSize: "12px",
                        padding: "4px 8px",
                        height: "28px",
                        minWidth: "28px"
                    }}
                />
            </Tooltip>
            <Tooltip title="View Invoice">
                <Button
                    type="default"
                    icon={<EyeOutlined />}
                    size="small"
                    onClick={() => handleViewClick(invoice)}
                    style={{
                        borderColor: "#004f7c",
                        color: "#004f7c",
                        fontSize: "12px",
                        padding: "4px 8px",
                        height: "28px",
                        minWidth: "28px"
                    }}
                />
            </Tooltip>
        </Space>
    );

    return (
        <div className="content-page">
            <div className="container-fluid">
                {/* Summary Cards */}
                <div className="row mb-4">
                    <div className="col-md-3">
                        <div className="card shadow-sm border-0" style={{ borderRadius: 12 }}>
                            <div className="card-body">
                                <div className="text-muted mb-1">Total Invoices</div>
                                <div className="d-flex align-items-center">
                                    <i className="ri-file-list-3-line" style={{ fontSize: 28, marginRight: 8, color: "#22314F" }}></i>
                                    <span style={{ fontSize: 24, color: "#22314F", fontWeight: 500 }}>{totalInvoices}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card shadow-sm border-0" style={{ borderRadius: 12 }}>
                            <div className="card-body">
                                <div className="text-muted mb-1">Total Amount</div>
                                <div style={{ fontSize: 24, color: "#22314F", fontWeight: 500 }}>
                                    {formatCurrency(totalAmount, invoices[0]?.currency || 'GBP', true)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card shadow-sm border-0" style={{ borderRadius: 12 }}>
                            <div className="card-body">
                                <div className="text-muted mb-1">Paid Amount</div>
                                <div style={{ fontSize: 24, color: "#3CB371", fontWeight: 500 }}>
                                    {formatCurrency(paidAmount, invoices[0]?.currency || 'GBP', true)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card shadow-sm border-0" style={{ borderRadius: 12 }}>
                            <div className="card-body">
                                <div className="text-muted mb-1">Pending Amount</div>
                                <div style={{ fontSize: 24, color: "#FFA500", fontWeight: 500 }}>
                                    {formatCurrency(pendingAmount, invoices[0]?.currency || 'GBP', true)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Search Bar */}
                <div className="row align-items-center justify-content-center">
                    <div className="col-5">
                        <div className="input-group mb-0">
                            <input
                                type="search"
                                className="form-control search-input"
                                placeholder="Keyword search..."
                                value={searchQuery}
                                onChange={e => {
                                    setSearchQuery(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                            <div className="input-group-append">
                                <button className="btn btn-primary" type="button" title="Search" rel="tooltip">
                                    <i className="ri-menu-search-line"></i>
                                </button>
                            </div>
                        </div>
                        <small className="form-text text-muted text-center mt-0">
                            <i className="ri-information-2-line"></i> Search by Invoice ID, Partner, Month, Year, Amount, Status
                        </small>
                    </div>
                </div>
                {/* Table */}
                <div className="row mt-3">
                    <div className="col-md-12">
                        <div className="table-responsive asset-information">
                            <table className="table grid">
                                <thead className="bg-light">
                                    <tr>
                                        <th>S.No.</th>
                                        <th>Invoice ID</th>
                                        <th>Partner</th>
                                        <th>Month</th>
                                        <th>Year</th>
                                        <th>Amount</th>
                                        <th>Paid</th>
                                        <th>Status</th>
                                        <th>Generated Date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={10} className="text-center py-4">
                                                <span className="loader"></span>
                                            </td>
                                        </tr>
                                    ) : currentRecords.length === 0 ? (
                                        <tr>
                                            <td colSpan={10} className="text-center py-4">
                                                <div className="no-data-wrapper">
                                                    <div className="no-data-found">
                                                        <i className="ri-file-damage-fill"></i>
                                                        <div className="bounce"><i className="ri-search-eye-line"></i></div>
                                                        <p>No Invoices Found</p>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        currentRecords.map((inv, idx) => (
                                            <tr key={inv.invoiceId}>
                                                <td>{idx + 1 + indexOfFirstRecord}</td>
                                                <td>{inv.invoiceId}</td>
                                                <td
                                                    style={{
                                                        maxWidth: '150px',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                    title={inv.partnerName ? `${inv.partnerName} (${inv.partner})` : inv.partner} // full text on hover
                                                >
                                                    {inv.partnerName ? `${inv.partnerName} (${inv.partner})` : inv.partner}
                                                </td>

                                                <td>{inv.month}</td>
                                                <td>{inv.year}</td>
                                                <td>
                                                    <span className="text-success font-weight-bold">
                                                        {formatCurrency(inv.amount, inv.currency)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-primary font-weight-bold">
                                                        {formatCurrency(inv.paid || 0, inv.currency)}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`badge border border-${inv.status === "Paid" ? "success text-success" : inv.status === "Rejected" ? "danger text-danger" : "warning text-warning"} status`}>
                                                        {inv.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    {inv.generatedDate ? moment(inv.generatedDate).format("DD-MM-YYYY") : "-"}
                                                </td>
                                                <td>
                                                    {renderActions(inv)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                {/* Pagination */}
                <div className="col-md-12">
                    <div className="container-fluid">
                        <div className="row border-top pt-3">
                            <div className="col-auto">
                                <p className="mt-1 mb-0">Records</p>
                            </div>
                            <div className="col-1.2 record-drop">
                                <select className="form-control" value={recordsPerPage} onChange={e => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                    <option value="5">5</option>
                                    <option value="10">10</option>
                                    <option value="15">15</option>
                                    <option value="20">20</option>
                                    <option value="25">25</option>
                                </select>
                            </div>
                            <div className="col">
                                <nav>
                                    <ul className="pagination justify-content-center">
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => paginate(1)} tabIndex="-1">
                                                <i className="ri-arrow-left-double-line"></i>
                                            </button>
                                        </li>
                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => paginate(currentPage - 1)} tabIndex="-1">
                                                <i className="ri-arrow-left-s-line"></i>
                                            </button>
                                        </li>
                                        {Array.from({ length: totalPages }, (_, index) => (
                                            <li key={index + 1} className={`page-item ${index + 1 === currentPage ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => paginate(index + 1)}>
                                                    {index + 1}
                                                </button>
                                            </li>
                                        ))}
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                                                <i className="ri-arrow-right-s-line"></i>
                                            </button>
                                        </li>
                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => paginate(totalPages)}>
                                                <i className="ri-arrow-right-double-fill"></i>
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                            <div className="col-auto">
                                <p className="mt-1 mb-0">Page Showing {currentPage} of {totalPages}</p>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Email Modal */}
                <Modal
                    title="Send Invoice via Email"
                    visible={isEmailModalVisible}
                    onCancel={handleEmailModalCancel}
                    footer={null}
                >
                    <Form
                        form={emailForm}
                        layout="vertical"
                        onFinish={handleEmailSend}
                    >
                        <Form.Item
                            name="email"
                            label="Recipient Email"
                            rules={[
                                { required: true, message: "Please enter recipient email" },
                                { type: "email", message: "Please enter a valid email" },
                            ]}
                        >
                            <Input placeholder="Enter email address" />
                        </Form.Item>
                        <Form.Item>
                            <Space>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={sendingEmail}
                                    style={{ backgroundColor: "#004f7c", borderColor: "#004f7c" }}
                                >
                                    Send
                                </Button>
                                <Button onClick={handleEmailModalCancel}>Cancel</Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Modal>
                {/* View Modal */}
                <Modal
                    title={`Invoice Details - ${selectedInvoice?.invoiceId}`}
                    visible={isViewModalVisible}
                    onCancel={handleViewModalCancel}
                    width={800}
                    footer={[
                        <Button key="close" onClick={handleViewModalCancel}>
                            Close
                        </Button>,
                        <Button
                            key="view"
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={() => handleGenerateInvoice(selectedInvoice)}
                            style={{ backgroundColor: "#004f7c", borderColor: "#004f7c" }}
                        >
                            View PDF
                        </Button>,
                    ]}
                >
                    {selectedInvoice && (
                        <div style={{ height: "600px", overflow: "auto" }}>
                            {selectedInvoice.invoiceFile ? (
                                <iframe
                                    src={selectedInvoice.invoiceFile}
                                    width="100%"
                                    height="100%"
                                    title={`Invoice ${selectedInvoice.invoiceId}`}
                                    style={{ border: "none" }}
                                />
                            ) : (
                                <p>No invoice file available for this invoice.</p>
                            )}
                        </div>
                    )}
                </Modal>
            </div>
        </div>
    );
}

export default PartnersDashboardPage;